import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import { useRecoilValue } from "recoil";

import { TextSm } from "components/atoms/text";
import Info from "components/layouts/Header/Info";
import Modal from "components/organisms/Modal";
import ModalPalette from "components/layouts/Header/ModalPalette";
import COLORS from "components/atoms/colors";
import HEADER_HEIGHT from "components/atoms/headerHeight";

import useUserMe from "apis/queries/useUserMe";
import useCompanyMe from "apis/queries/useCompanyMe";
import useProject from "apis/queries/project/useProject";

import sceneIndexAtom from "stores/sceneIndex";

import logo from "assets/images/common/logo-white.svg";
import { ReactComponent as IconPalette } from "assets/images/common/palette.svg";
import { ReactComponent as IconBack } from "assets/images/common/back.svg";

const RESPONSIVE_PAGES = ["/beta/signup", "/signup"];

const Header = () => {
  const { pathname } = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const userMe = useUserMe();
  const companyMe = useCompanyMe();
  const projectId = Number(params.id);
  const { project } = useProject({ projectId });
  const sceneIndex = useRecoilValue(sceneIndexAtom);

  const isResponsivePage = RESPONSIVE_PAGES.includes(pathname);
  const currentScene = project?.scenes[sceneIndex];

  return (
    <Container responsive={isResponsivePage}>
      <Inner>
        {(() => {
          if (pathname === `/project/${params.id}`) {
            return (
              <div className="flex items-center gap-3">
                <button onClick={() => navigate(-1)}>
                  <IconBack />
                </button>
                <Title>
                  {project?.name} / {currentScene?.title} (Scene{" "}
                  {sceneIndex + 1})
                </Title>
              </div>
            );
          }

          return (
            <h1
              onClick={() => window.open("https://macodi.co.kr", "_blank")}
              className="cursor-pointer"
            >
              <img
                src={logo}
                alt="macodi"
                className={`${isResponsivePage && "md:h-[14px]"}`}
              />
            </h1>
          );
        })()}

        {(() => {
          if (pathname === "/beta/signup") return <></>;

          if (pathname === "/signup") {
            return (
              <button onClick={() => navigate("/login")}>
                <TextSm
                  className={`text-white ${isResponsivePage && "md:!text-xs"}`}
                >
                  로그인
                </TextSm>
              </button>
            );
          }

          if (pathname === "/") {
            return <Info name={companyMe?.name || userMe?.name} />;
          }

          return (
            <div className="flex items-center gap-3">
              <Info name={companyMe?.name || userMe?.name} />
              <Modal
                activator={(onOpen) => (
                  <BtnPalette onClick={onOpen}>
                    My Palette
                    <IconPalette />
                  </BtnPalette>
                )}
                modal={(onClose) => <ModalPalette onClose={onClose} />}
              />
            </div>
          );
        })()}
      </Inner>
    </Container>
  );
};

const Container = styled.header<{ responsive?: boolean }>`
  height: ${HEADER_HEIGHT}px;
  background: ${COLORS.primary.active};
  padding: 0 60px;

  ${({ responsive }) =>
    responsive &&
    css`
      @media (max-width: 1023px) {
        padding: 0 16px;
      }
      @media (max-width: 767px) {
        height: 44px;
      }
    `}
`;

const Inner = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #fff;
`;

const BtnPalette = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  background-color: #fff;
  border-radius: 36px;
  font-size: 14px;
  font-weight: 700;
  color: ${COLORS.primary.active};
`;

export default Header;
